import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from "@angular/core";
import { RouterLink, RouterOutlet } from "@angular/router";
import { AsyncPipe, CommonModule } from "@angular/common";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatCardModule } from "@angular/material/card";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { FavoritesComponent } from "./favorites/favorites.component";
import { FavoritesService } from "../../../../../shared/src/lib/services/favorites.service";
import { combineLatest, map, Observable, Subject } from "rxjs";
import { NavService } from "../../../../../shared/src/lib/services/nav.service";
import { MarketService } from "../../../../../shared/src/lib/services/market.service";
import { TranslateModule } from "@ngx-translate/core";
import { LoaderService } from "../../../../../shared/src/lib/services/loader.service";
import { NavItemLoaderComponent } from "./navigation-item/nav-item-loader/nav-item-loader.component";
import { NextEventsService } from "../content/home/next-horse-race/next-events.service";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-sports-bar",
  templateUrl: "./sports-bar.component.html",
  styleUrl: "./sports-bar.component.scss",
  imports: [
    CommonModule,
    MatExpansionModule,
    MatProgressBarModule,
    MatCardModule,
    FormsModule,
    ReactiveFormsModule,
    RouterOutlet,
    RouterLink,
    MatIconModule,
    FavoritesComponent,
    TranslateModule,
    NavItemLoaderComponent,
    AsyncPipe,
  ],
})
export class SportsBarComponent implements OnInit, OnDestroy {
  readonly destroy$ = new Subject<void>();
  favoritesService = inject(FavoritesService);
  marketService = inject(MarketService);
  nextEventsService = inject(NextEventsService);
  navService = inject(NavService);
  loader = inject(LoaderService);
  hasFavorites: boolean = false;
  $loading!: Observable<boolean>;

  // Local state to avoid change detection conflicts
  public localSportsPanelOpen = false;
  public localFavoritesPanelOpen = false;

  ngOnInit() {
    this.$loading = combineLatest([
      this.loader.isLoading("/bf/next-race"),
      this.loader.isLoading("/v1/api/navigation_list"),
      this.loader.isLoading("/navigation/event"),
    ]).pipe(map(([loadingNextRace, loadingNavigationList, loadingNavEvents]) => loadingNextRace || loadingNavigationList || loadingNavEvents));

    this.favoritesService
      .getFavoriteItems()
      .pipe(takeUntil(this.destroy$))
      .subscribe((items) => {
        this.hasFavorites = items.length > 0;
      });

    // Sync with service state on init

    this.navService.openSportsPanel.pipe(takeUntil(this.destroy$)).subscribe((open) => {
      this.localSportsPanelOpen = open;
    });

    this.navService.openFavoritesPanel.pipe(takeUntil(this.destroy$)).subscribe((open) => {
      this.localFavoritesPanelOpen = open;
    });
  }

  handleClick() {
    if (window.innerWidth < 1280) {
      this.navService.openSportsBar.next(false);
    }
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  handleSportsPanel(state: string) {
    // Update local state immediately
    this.localSportsPanelOpen = state === "opened";

    // Defer service update to avoid change detection conflict
    Promise.resolve().then(() => {
      if (state === "opened") {
        this.navService.setOpenSportsPanel(true);
        this.navService.setOpenFavoritesPanel(false);
      } else {
        this.navService.setOpenSportsPanel(false);
      }
    });
  }

  handleFavoritesPanel(state: string) {
    // Update local state immediately
    this.localFavoritesPanelOpen = state === "opened";

    // Defer service update to avoid change detection conflict
    Promise.resolve().then(() => {
      if (state === "opened") {
        this.navService.setOpenFavoritesPanel(true);
        this.navService.setOpenSportsPanel(false);
      } else {
        this.navService.setOpenFavoritesPanel(false);
        this.navService.setOpenSportsPanel(true);
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
